import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";

type CommunitySourceType = Database["public"]["Enums"]["community_source_type"];
type IngestionCadence = Database["public"]["Enums"]["ingestion_cadence"];

interface CommunitySource {
  id: string;
  name: string;
  source_type: CommunitySourceType;
  endpoint_url: string;
  is_enabled: boolean;
  keywords: string[] | null;
  ontario_only: boolean;
  notes: string | null;
}

interface IngestionSettings {
  id: string;
  cadence: IngestionCadence;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
}

interface Props {
  settings: IngestionSettings | null;
  sources: CommunitySource[];
  isLoadingSettings: boolean;
  isLoadingSources: boolean;
}

export function CommunitySignalsSettings({ settings, sources, isLoadingSettings, isLoadingSources }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdatingCadence, setIsUpdatingCadence] = useState(false);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [editingSource, setEditingSource] = useState<CommunitySource | null>(null);
  const [isSavingSource, setIsSavingSource] = useState(false);
  const [isDeletingSource, setIsDeletingSource] = useState<string | null>(null);

  // Form state for source dialog
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState<CommunitySourceType>("RFD");
  const [formUrl, setFormUrl] = useState("");
  const [formEnabled, setFormEnabled] = useState(true);
  const [formKeywords, setFormKeywords] = useState("");
  const [formOntarioOnly, setFormOntarioOnly] = useState(true);
  const [formNotes, setFormNotes] = useState("");

  const handleCadenceChange = async (newCadence: IngestionCadence) => {
    if (!settings) return;
    setIsUpdatingCadence(true);
    try {
      const { error } = await supabase
        .from("community_ingestion_settings")
        .update({ cadence: newCadence })
        .eq("id", settings.id);

      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["community-ingestion-settings"] });
      toast({ title: "Cadence updated" });
    } catch (error) {
      toast({ 
        title: "Error updating cadence", 
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive" 
      });
    } finally {
      setIsUpdatingCadence(false);
    }
  };

  const openAddDialog = () => {
    setEditingSource(null);
    setFormName("");
    setFormType("RFD");
    setFormUrl("");
    setFormEnabled(true);
    setFormKeywords("");
    setFormOntarioOnly(true);
    setFormNotes("");
    setShowSourceDialog(true);
  };

  const openEditDialog = (source: CommunitySource) => {
    setEditingSource(source);
    setFormName(source.name);
    setFormType(source.source_type);
    setFormUrl(source.endpoint_url);
    setFormEnabled(source.is_enabled);
    setFormKeywords(source.keywords?.join(", ") || "");
    setFormOntarioOnly(source.ontario_only);
    setFormNotes(source.notes || "");
    setShowSourceDialog(true);
  };

  const handleSaveSource = async () => {
    if (!formName || !formUrl) {
      toast({ title: "Name and URL are required", variant: "destructive" });
      return;
    }

    setIsSavingSource(true);
    try {
      const keywords = formKeywords
        .split(",")
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const sourceData = {
        name: formName,
        source_type: formType as CommunitySourceType,
        endpoint_url: formUrl,
        is_enabled: formEnabled,
        keywords: keywords.length > 0 ? keywords : null,
        ontario_only: formOntarioOnly,
        notes: formNotes || null
      };

      if (editingSource) {
        const { error } = await supabase
          .from("community_sources")
          .update(sourceData)
          .eq("id", editingSource.id);
        if (error) throw error;
        toast({ title: "Source updated" });
      } else {
        const { error } = await supabase
          .from("community_sources")
          .insert(sourceData);
        if (error) throw error;
        toast({ title: "Source added" });
      }

      queryClient.invalidateQueries({ queryKey: ["community-sources"] });
      setShowSourceDialog(false);
    } catch (error) {
      toast({
        title: "Error saving source",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsSavingSource(false);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    setIsDeletingSource(sourceId);
    try {
      const { error } = await supabase
        .from("community_sources")
        .delete()
        .eq("id", sourceId);

      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["community-sources"] });
      toast({ title: "Source deleted" });
    } catch (error) {
      toast({
        title: "Error deleting source",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsDeletingSource(null);
    }
  };

  const getSourceTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      RFD: "bg-red-500",
      REDDIT: "bg-orange-500",
      GENERIC_RSS: "bg-blue-500",
      GENERIC_HTML: "bg-purple-500"
    };
    return <Badge className={colors[type] || "bg-gray-500"}>{type}</Badge>;
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cadence Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ingestion Cadence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Label htmlFor="cadence">Schedule:</Label>
          <Select
              value={settings?.cadence || "H12"}
              onValueChange={(val) => handleCadenceChange(val as IngestionCadence)}
              disabled={isUpdatingCadence}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="H12">Every 12 hours</SelectItem>
                <SelectItem value="H24">Every 24 hours</SelectItem>
                <SelectItem value="D7">Every 7 days</SelectItem>
                <SelectItem value="NONE">Disabled</SelectItem>
                <SelectItem value="ON_BATCH_RUN">On batch run</SelectItem>
              </SelectContent>
            </Select>
            {isUpdatingCadence && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        </CardContent>
      </Card>

      {/* Sources Configuration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Sources</CardTitle>
          <Dialog open={showSourceDialog} onOpenChange={setShowSourceDialog}>
            <DialogTrigger asChild>
              <Button onClick={openAddDialog} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingSource ? "Edit Source" : "Add Source"}</DialogTitle>
                <DialogDescription>
                  Configure a community source for signal ingestion
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Source Type</Label>
                  <Select value={formType} onValueChange={(val) => setFormType(val as CommunitySourceType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RFD">RedFlagDeals</SelectItem>
                      <SelectItem value="REDDIT">Reddit</SelectItem>
                      <SelectItem value="GENERIC_RSS">Generic RSS</SelectItem>
                      <SelectItem value="GENERIC_HTML">Generic HTML</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g., RFD Hot Deals"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url">Endpoint URL</Label>
                  <Input
                    id="url"
                    value={formUrl}
                    onChange={(e) => setFormUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                  <Input
                    id="keywords"
                    value={formKeywords}
                    onChange={(e) => setFormKeywords(e.target.value)}
                    placeholder="gift card, giftcard, bonus"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="enabled">Enabled</Label>
                  <Switch
                    id="enabled"
                    checked={formEnabled}
                    onCheckedChange={setFormEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="ontario">Ontario Only</Label>
                  <Switch
                    id="ontario"
                    checked={formOntarioOnly}
                    onCheckedChange={setFormOntarioOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSourceDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveSource} disabled={isSavingSource}>
                  {isSavingSource && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {editingSource ? "Update" : "Add"} Source
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {isLoadingSources ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : sources.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No sources configured. Add a source to start ingesting signals.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Keywords</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="font-medium">{source.name}</TableCell>
                    <TableCell>{getSourceTypeBadge(source.source_type)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {source.keywords?.join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={source.is_enabled ? "default" : "secondary"}>
                        {source.is_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(source)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteSource(source.id)}
                          disabled={isDeletingSource === source.id}
                        >
                          {isDeletingSource === source.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
